// Templates Route
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import { Plus, FileText, Copy, Trash2, MoreVertical, Star, Banknote, Building2, Calculator, CheckCircle, CreditCard, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { TemplateForm } from '@/components/templates';
import { getTemplateFeatures } from '@/components/templates/TemplateSelector';

export const Route = createFileRoute('/templates/')({
  component: Templates,
});

function Templates() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [templateToDelete, setTemplateToDelete] = useState<any | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.templates.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setTemplateToDelete(null); // Close modal on success
      toast({ title: "Template deleted successfully" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.templates.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const confirmDelete = () => {
    if (templateToDelete) {
      deleteMutation.mutate(templateToDelete.id);
    }
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; name: string; description?: string; isDefault: boolean }) => 
      api.templates.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast({ title: 'Template Updated' });
    },
  });

  const handleUpdateTemplate = (id: string, payload: any) => {
    updateMutation.mutate({ id, ...payload });
  };

  const templates = data?.data || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Templates</h1>
          <p className="text-muted-foreground mt-1">
            Manage your analysis templates
          </p>
        </div>
        <button
          onClick={() => navigate({ to: '/templates/new', search: { edit: undefined } })}
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Template
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-xl" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-medium mb-2">No templates yet</p>
          <p className="text-muted-foreground mb-6">
            Start an analysis and save it as a template to reuse configurations
          </p>
          <button
            onClick={() => navigate({ to: '/workflow' })}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-400"
          >
            <Plus className="w-5 h-5" />
            Start New Analysis
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onDuplicate={() => duplicateMutation.mutate(template.id)}
              onDelete={() => setTemplateToDelete(template)}
              onUpdate={(payload) => handleUpdateTemplate(template.id, payload)}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {templateToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm" 
            onClick={() => !deleteMutation.isPending && setTemplateToDelete(null)} 
          />
          
          {/* Modal Content */}
          <div className="relative bg-card border border-border w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4 text-destructive">
              <div className="p-2 bg-destructive/10 rounded-full">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete Template</h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Are you sure you want to delete the template: <span className="font-semibold text-foreground">"{templateToDelete.name}"</span>? 
              This will remove the saved configuration <span className="text-destructive font-bold">permanently</span>. This action cannot be undone.
            </p>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button 
                disabled={deleteMutation.isPending}
                onClick={() => setTemplateToDelete(null)} 
                className="px-4 py-2 rounded-xl text-sm font-medium hover:bg-muted transition-colors border border-border disabled:opacity-50"
              >
                Cancel
              </button>
              <button 
                disabled={deleteMutation.isPending}
                onClick={confirmDelete} 
                className="px-4 py-2 bg-destructive text-white rounded-xl text-sm font-bold hover:bg-destructive/90 transition-all shadow-lg shadow-destructive/20 disabled:opacity-50 flex items-center justify-center min-w-[140px]"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface TemplateCardProps {
  template: any;
  onDuplicate: () => void;
  onDelete: () => void;
}

function TemplateCard({
  template,
  onDuplicate,
  onDelete,
  onUpdate
}: TemplateCardProps & { onUpdate: (payload: any) => void }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false); // State for the edit modal

  const config = template.config || {};
  const modules = config.modules || {};

  const countFeatures = () => {
    let count = 0;
    if (config.global || config.summaryPreferences) count++;
    if (modules.financial?.enabled) count++;
    if (modules.financial?.selectedMetricIds?.length > 0) count++;
    if (modules.creditReport?.enabled) count++;
    if (modules.bank?.enabled) count++;
    if (modules.corporate?.enabled) count++;
    return count;
  };

  return (
    <>
      <div 
        onClick={() => setEditOpen(true)} // Click card to edit
        className="bg-card rounded-xl p-6 border border-border hover:border-accent-500/50 transition-all group flex flex-col h-full cursor-pointer relative"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="p-3 bg-accent-500/10 text-accent-500 rounded-lg">
            <FileText className="w-6 h-6" />
          </div>
          <div className="flex items-center gap-2">
            {template.isDefault && (
              <Badge variant="secondary" className="gap-1 bg-warning/10 text-warning border-none">
                <Star className="w-3 h-3 fill-current" />
                Default
              </Badge>
            )}
            
            {/* Options Menu */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent opening the edit modal
                  setMenuOpen(!menuOpen);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg shadow-lg border border-border z-20 overflow-hidden">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        navigate({ to: '/templates/new', search: { edit: template.id } });
                      }}
                      className="flex items-center gap-2 px-4 py-2 w-full hover:bg-muted transition-colors text-sm"
                    >
                      <Pencil className="w-4 h-4" /> Edit Configuration
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDuplicate(); }}
                      className="flex items-center gap-2 px-4 py-2 w-full hover:bg-muted transition-colors text-sm"
                    >
                      <Copy className="w-4 h-4" /> Duplicate
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        onDelete();
                      }}
                      className="flex items-center gap-2 px-4 py-2 w-full text-destructive hover:bg-destructive/10 transition-colors text-sm"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1 group-hover:text-accent-500 transition-colors truncate">
            {template.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 min-h-[40px]">
            {template.description || 'No description provided'}
          </p>

          <div className="flex flex-wrap gap-2 mb-6">
             {config.global && <FileText className="w-4 h-4 text-accent-500/60" />}
             {modules.financial?.enabled && <Calculator className="w-4 h-4 text-blue-500/60" />}
             {modules.creditReport?.enabled && <CreditCard className="w-4 h-4 text-purple-500/60" />}
             {modules.bank?.enabled && <Banknote className="w-4 h-4 text-green-500/60" />}
             {modules.corporate?.enabled && <Building2 className="w-4 h-4 text-orange-500/60" />}
          </div>
        </div>

        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider font-medium text-muted-foreground pt-4 border-t border-border/50">
          <span>Created {formatDate(template.createdAt)}</span>
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            {countFeatures()} configured items
          </span>
        </div>
      </div>

      {/* Edit Dialog using existing TemplateForm */}
      {editOpen && (
        <TemplateForm
          open={editOpen}
          onOpenChange={setEditOpen}
          initialData={{
            name: template.name,
            description: template.description,
            isDefault: template.isDefault
          }}
          onDelete={() => {
            onDelete(); // Calls the onDelete passed into TemplateCard
            setEditOpen(false);
          }}
          onSave={(payload) => {
            onUpdate(payload);
            setEditOpen(false);
          }}
          // FIX: Map the features to add the required 'included' property
          includedItems={getTemplateFeatures(template.config).map(feature => ({
            ...feature,
            included: true
          }))} 
        />
      )}
    </>
  );
}
